<template>
  <section class="space-y-3">
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-baseline gap-2.5">
        <h3 class="text-[17px] font-extrabold tracking-tight">{{ t('member.title') }}</h3>
        <span v-if="!loading && !error" class="font-mono text-sm text-muted-foreground">
          {{ members.length }}
        </span>
      </div>
      <Button size="sm" @click="openAddForm">
        {{ t('member.addMember') }}
      </Button>
    </div>
    <p class="text-sm text-muted-foreground">{{ t('member.subtitle') }}</p>

    <p v-if="loading" class="text-muted-foreground text-sm">
      {{ t('common.loading') }}
    </p>
    <p v-else-if="error" class="text-destructive text-sm">{{ error }}</p>
    <p v-else-if="members.length === 0" class="text-muted-foreground text-sm">
      {{ t('member.noMembers') }}
    </p>
    <div v-else class="overflow-hidden rounded-2xl border bg-white">
      <div
        v-for="member in members"
        :key="member.id"
        class="mrow flex flex-col items-start gap-3 border-t p-4 transition-colors first:border-t-0 sm:flex-row sm:items-center sm:justify-between bg-white"
      >
        <div class="flex min-w-0 flex-1 items-center gap-3">
          <AvatarInitials :name="member.userName" size="sm" />
          <div class="min-w-0 flex-1">
            <p class="truncate font-bold">{{ member.userName }}</p>
            <p class="truncate font-mono text-xs text-muted-foreground">{{ member.userEmail }}</p>
          </div>
        </div>
        <div class="flex w-full items-center justify-between sm:ml-4 sm:w-auto sm:justify-end sm:gap-2 sm:shrink-0">
          <Badge :variant="roleVariant(member.role)">{{ roleLabel(member.role) }}</Badge>
          <Button
            variant="ghost"
            size="sm"
            class="h-8 w-8 p-0"
            @click="openEditForm(member)"
          >
            <Pencil class="w-3.5 h-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            class="h-8 w-8 p-0 text-destructive hover:text-destructive"
            @click="confirmRemove(member)"
          >
            <Trash2 class="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>

    <MemberForm
      :open="showMemberForm"
      :project-id="projectId"
      :initial-data="selectedMember"
      :existing-member-user-ids="members.map((m) => m.userId)"
      @submit="handleMemberSubmit"
      @close="closeMemberForm"
    />
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { Pencil, Trash2 } from 'lucide-vue-next';
import {
  useShellServices,
  useTaskboltTranslation,
} from '@/shared/composables/useShellServices';
import {
  getProjectMembers,
  addProjectMember,
  updateMemberRole,
  removeProjectMember,
} from '@/shared/services';
import { roleDisplayName } from '@/shared/lib/role';
import {
  type ProjectMember,
  type AddProjectMemberPayload,
  type UpdateMemberRolePayload,
} from '@/shared/types/member';
import { Button } from '@/shared/components/ui/button';
import { Badge, type BadgeVariants } from '@/shared/components/ui/badge';
import { AvatarInitials } from '@/shared/components/ui/avatar';
import MemberForm from './MemberForm.vue';

const props = defineProps<{ projectId: string }>();

const { getApiClient, getToastService } = useShellServices();
const { t } = useTaskboltTranslation();

const members = ref<ProjectMember[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);
const showMemberForm = ref(false);
const selectedMember = ref<ProjectMember | null>(null);

// Roles are dynamic tenant data now (not a fixed enum), so the badge variant
// is derived from the role name the same way avatar colors are derived
// from a person's name — a stable hash, not a lookup table.
const ROLE_BADGE_VARIANTS: NonNullable<BadgeVariants['variant']>[] = [
  'purple',
  'brand',
  'success',
  'neutral',
  'warning',
];

function roleVariant(role: string): BadgeVariants['variant'] {
  const hash = [...role].reduce((a, c) => a + c.charCodeAt(0), 0);
  return ROLE_BADGE_VARIANTS[hash % ROLE_BADGE_VARIANTS.length];
}

function roleLabel(role: string): string {
  return roleDisplayName(t, role);
}

onMounted(fetchMembers);
// projectId can change live (e.g. Configure home reuses this bound to the
// sidebar's selectedProjectId), unlike ProjectDetailPage where it only ever
// changed via a route change that remounts the whole page.
watch(() => props.projectId, fetchMembers);

async function fetchMembers() {
  const apiClient = getApiClient();
  if (!apiClient) {
    error.value = t('toast.apiClientUnavailable');
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    members.value = await getProjectMembers(apiClient, props.projectId);
  } catch (err) {
    error.value = err instanceof Error ? err.message : t('toast.genericError');
  } finally {
    loading.value = false;
  }
}

function openAddForm() {
  selectedMember.value = null;
  showMemberForm.value = true;
}

function openEditForm(member: ProjectMember) {
  selectedMember.value = member;
  showMemberForm.value = true;
}

function closeMemberForm() {
  showMemberForm.value = false;
  selectedMember.value = null;
}

async function handleMemberSubmit(
  data: AddProjectMemberPayload | UpdateMemberRolePayload,
  isEdit: boolean,
) {
  const apiClient = getApiClient();
  const toast = getToastService();

  if (!apiClient) {
    toast?.error(t('toast.apiClientUnavailable'));
    return;
  }

  try {
    if (isEdit && selectedMember.value) {
      await updateMemberRole(
        apiClient,
        props.projectId,
        selectedMember.value.id,
        data as UpdateMemberRolePayload,
      );
      toast?.success(t('toast.memberUpdated'));
    } else {
      await addProjectMember(apiClient, props.projectId, data as AddProjectMemberPayload);
      toast?.success(t('toast.memberAdded'));
    }
    closeMemberForm();
    await fetchMembers();
  } catch (err) {
    const message = err instanceof Error ? err.message : t('toast.genericError');
    toast?.error(isEdit ? t('toast.memberUpdateFailed') : t('toast.memberAddFailed'));
    console.error('Member submit error:', message);
  }
}

async function confirmRemove(member: ProjectMember) {
  if (!window.confirm(`${t('member.confirmRemovePrefix')} "${member.userName}" ${t('member.confirmRemoveSuffix')}`)) return;

  const apiClient = getApiClient();
  const toast = getToastService();

  if (!apiClient) {
    toast?.error(t('toast.apiClientUnavailable'));
    return;
  }

  try {
    await removeProjectMember(apiClient, props.projectId, member.id);
    toast?.success(t('toast.memberRemoved'));
    await fetchMembers();
  } catch (err) {
    const message = err instanceof Error ? err.message : t('toast.genericError');
    toast?.error(t('toast.memberRemoveFailed'));
    console.error('Member remove error:', message);
  }
}
</script>
